// React Dependencies
import React, { useState, useEffect } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Typography, TextField, Button, Chip, CircularProgress } from '@material-ui/core';
import { toast } from 'react-toastify';

// Internal Dependencies
import { EscapeSingleQuote, Sleep, StringToLowercase, Trim } from '../../../common/util';
import { IsEmpty } from '../../../common/checks';
import apiService from '../../../services.js/apiService';
import Colours from '../../../styles/colours';
import MasterNameChips from '../../common/MasterNameChips';

const MasterNameForm = ({ selected, onAdd, onUpdate }) => {
  const [oldMasterName, setOldMasterName] = useState('');
  const [masterName, setMasterName] = useState('');
  const [locations, setLocations] = useState([]);
  const [suggestedLocations, setSuggestedLocations] = useState([]);
  const [isSearchingLocations, setIsSearchingLocations] = useState(false);

  const onSaveHandler = () => {
    if (masterName.length === 0) {
      return toast.error('Please enter master name');
    } else if (locations === null || locations.length === 0) {
      return toast.error('Please add at least one location name');
    }

    const payload = {
      oldMasterName,
      masterName,
      names: locations,
    };

    if (selected) {
      onUpdate(payload);
    } else {
      onAdd(payload);
    }
  };

  const onNameChangeHandler = async (value) => {
    const nameSearch = EscapeSingleQuote(Trim(value));
    const reviewLocations = [];

    try {
      setIsSearchingLocations(true);
      setSuggestedLocations([]);

      if (!IsEmpty(value)) {
        let result = await apiService.getReviewLocationsSuggest(nameSearch);
        result = _.uniqBy(result, 'name');

        // limit max 4 records for display
        if (result.length > 10) {
          result = _.dropRight(result, result.length - 4);
        }

        const reviewLocationNames = _.map(result, (reviewLocation) => reviewLocation.name);

        // format the tag names needed by server
        const tagNamesDelimited = _.join(reviewLocationNames, '||');
        const tags = await apiService.getTagsByNames({ tagNames: tagNamesDelimited });

        for (const reviewLocation of result) {
          const location = { ...reviewLocation, isApproved: null };
          const tagFound = _.find(tags, (tag) => StringToLowercase(tag.name) === StringToLowercase(location.name));

          if (!IsEmpty(tagFound)) {
            reviewLocations.push({ ...location, isApproved: tagFound.isApproved });
          } else {
            reviewLocations.push(location);
          }
        }
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      await Sleep(300);
      const filteredSuggestedLocations = _.filter(reviewLocations, (location) => !locations.includes(location.name));

      setIsSearchingLocations(false);
      setSuggestedLocations(filteredSuggestedLocations);
      setMasterName(nameSearch);
    }
  };

  const nameDebounce = _.debounce(onNameChangeHandler, 500);

  const addLocationNameHandler = (location) => {
    const { name } = location;

    setLocations((prevLocation) => {
      const uniqueLocations = _.uniq(_.union([...prevLocation], [name]));
      return uniqueLocations;
    });
  };

  useEffect(() => {
    if (selected !== null) {
      const locationNames = _.uniq(selected.names);

      setOldMasterName(selected.masterName);
      setMasterName(selected.masterName);
      setLocations(locationNames);
    }
  }, []);

  useEffect(() => {
    const filteredSuggestedLocations = _.filter(suggestedLocations, (location) => !locations.includes(location.name));
    setSuggestedLocations(filteredSuggestedLocations);
  }, [locations]);

  return (
    <div>
      <div className="w-75 mb-3">
        <Typography variant="h6" color="inherit">
          Master Name
        </Typography>
        <TextField
          className="mt-2"
          variant="outlined"
          size="small"
          fullWidth
          defaultValue={selected !== null ? selected.masterName : ''}
          onChange={({ target }) => nameDebounce(target.value)}
        />
      </div>

      <div className="w-100 mb-3">
        <Typography variant="h6" color="inherit" className="">
          Review custom location names
        </Typography>
        <Typography variant="subtitle1" color="inherit" className="">
          Similar names without master (select to add to `Added` list)
        </Typography>
        { isSearchingLocations && (<CircularProgress className="m-3" color="secondary" size="2rem" />) }
        { !IsEmpty(suggestedLocations) && (
          <div className="mt-2 d-flex flex-row flex-wrap" style={{}}>
            {
            _.map((suggestedLocations), (location, index) => (
              <Chip
                key={`${location.name}-${index}` || `option-${index}`}
                variant="outlined"
                label={location.name}
                className="mr-3"
                style={{
                  background:
                  location.isApproved === true ? '#2ecc71'
                    : location.isApproved === false ? '#e74c3c'
                      : '#95a5a6',
                  color:        Colours.White,
                  borderColor:  Colours.White,
                  fontSize:     14,
                  cursor:       'pointer',
                  marginBottom: '4px',
                }}
                onClick={() => addLocationNameHandler(location)}
              />
            ))
            }
          </div>
        ) }
      </div>

      <div className="w-100 mb-3">
        <Typography variant="h6" color="inherit" className="">
          Added
        </Typography>
        <MasterNameChips
          data={locations || []}
          onChange={(values) => setLocations(values)}
        />
      </div>

      <div className="d-flex justify-content-end mb-3">
        <Button onClick={onSaveHandler} variant="contained">
          {selected ? 'Update' : 'Add'}
        </Button>
      </div>

    </div>
  );
};

export default MasterNameForm;

MasterNameForm.propTypes = {
  selected: PropTypes.shape(Object),
  onAdd:    PropTypes.func,
  onUpdate: PropTypes.func,
};

MasterNameForm.defaultProps = {
  selected: null,
  onAdd:    () => {},
  onUpdate: () => {},
};
